import React from 'react';
import { addSideAction, editSideNameAction, removeSideAction, useProductConfigurationState } from '../../../state';
import useNotFirstRenderEffect from 'src/shared/hooks/useNotFirstRender';

export type ResizableSideType = 'single' | 'double-syn' | 'double' | 'multiple' | 'multiplesyn';

interface IResizablePrintAreaContext {
    sideType: ResizableSideType;
    setSideType: (sideType: ResizableSideType) => void;
}

const ResizablePrintAreaContext = React.createContext<IResizablePrintAreaContext | null>(null);

export const ResizablePrintAreaContextProvider = ({
    children,
    initialSideType = 'single'
}: {
    children: React.ReactNode;
    initialSideType?: ResizableSideType;
}) => {
    const [sideType, setSideType] = React.useState<ResizableSideType>(initialSideType);
    const { state, dispatch } = useProductConfigurationState();

    useNotFirstRenderEffect(() => {
        const sides = state.sides;
        const modelColor = state.product.colors.find((x) => x.colorID === 1);
        if (!modelColor) return;
        const firstModelSide = modelColor.sides.find((x) => x.originalSideGuid === sides[0].guid);
        if (!firstModelSide) return;

        const sidesToRemove = sides.slice(1);
        switch (sideType) {
            case 'single':
                sidesToRemove.forEach((side) => dispatch(removeSideAction(side)));
                break;
            case 'double-syn':
            case 'double':
                sidesToRemove.forEach((side) => dispatch(removeSideAction(side)));
                dispatch(editSideNameAction(firstModelSide, 'Front'));
                dispatch(addSideAction('Back'));
                break;
            case 'multiplesyn':
            case 'multiple':
                sidesToRemove.forEach((side) => dispatch(removeSideAction(side)));
                dispatch(editSideNameAction(firstModelSide, 'Side 1'));
                dispatch(addSideAction('Side 2'));
                break;
            default:
                break;
        }
    }, [sideType]);

    return <ResizablePrintAreaContext.Provider value={{ sideType, setSideType }}>{children}</ResizablePrintAreaContext.Provider>;
};

export const useResizablePrintAreaContext = () => {
    const context = React.useContext(ResizablePrintAreaContext);
    if (!context) {
        throw new Error('useResizablePrintAreaContext must be used within a ResizablePrintAreaProvider');
    }
    return context;
};
